import {
	Component,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	NgZone,
	SimpleChanges
} from '@angular/core';

import { IVbSource } from 'vbrick-player-src/IVbSource';
import { VideogularComponent } from 'vbrick-player-src/videogular/Videogular.Component';

import { VBrick360MouseHelper } from './VBrick360MouseHelper';
import { VBrick360Viewer } from './VBrick360Viewer';

import styles from './Vg360.module.less';

// <reference types="resize-observer-polyfill/src/index.d.ts" />

@Component({
	selector: 'vg-360',
	template: `
		<div
			[ngClass]="styles.rotateControls"
			*ngIf="isEnabled && viewer">
			<div [ngClass]="styles.container">
				<div
					[ngClass]="styles.centerLabel"
					[hidden]="!viewer.isCentered">
					<span class="vb-icon vb-icon-360-indicator"></span>
				</div>

				<button
					[ngClass]="styles.centerButton"
					type="button"
					[hidden]="viewer.isCentered"
					(click)="centralize()">
					<span
						class="vb-icon vb-icon-reset"
						[ngClass]="styles.resetIcon"
						[title]="translations.reset360">
					</span>
				</button>

				<button
					[ngClass]="styles.left"
					type="button"
					(mousedown)="onLeftClick()">
					<span class="glyphicon glyphicon-chevron-left"></span>
				</button>

				<button
					[ngClass]="styles.up"
					type="button"
					(mousedown)="onUpClick()">
					<span class="glyphicon glyphicon-chevron-up"></span>
				</button>

				<button
					[ngClass]="styles.right"
					type="button"
					(mousedown)="onRightClick()">
					<span class="glyphicon glyphicon-chevron-right"></span>
				</button>

				<button
					[ngClass]="styles.down"
					type="button"
					(mousedown)="onDownClick()">
					<span class="glyphicon glyphicon-chevron-down"></span>
				</button>
			</div>
		</div>
	`
})
export class Vg360Component implements OnInit, OnDestroy, OnChanges {
	@Input() public activeSource: IVbSource;
	@Input() public isEnabled: boolean;
	@Input() public translations: any;

	public readonly styles = styles;

	private mouseHelper: VBrick360MouseHelper;
	private observer: ResizeObserver;
	private observerTarget: HTMLElement;
	private resizeObserverPromise: Promise<any>;
	private rotateIntervalRef: number;
	private vgElement: HTMLElement;
	public viewer: VBrick360Viewer;

	private readonly ROTATION_STEP: number = 10;

	constructor(
		private vgApi: VideogularComponent,
		private zone: NgZone
	) {}

	public ngOnInit(): void {
		this.vgElement = this.vgApi.videogularElement;
	}

	public ngOnChanges(changes: SimpleChanges): void {
		if (changes.activeSource) {
			this.onActiveSourceChange();
		}

		if (changes.isEnabled) {
			this.onIsEnabledChange();
		}
	}

	public ngOnDestroy(): void {
		this.stop();
	}

	public centralize(): void {
		this.viewer.centralize();
	}

	public onDownClick(): void {
		this.startRotateInterval(this.ROTATION_STEP, 0);
	}

	public onLeftClick(): void {
		this.startRotateInterval(0, -1 * this.ROTATION_STEP);
	}

	public onResize(): void {
		const { height, width } = this.observerTarget.getBoundingClientRect();

		this.viewer.setSize(width, height);
	}

	public onRightClick(): void {
		this.startRotateInterval(0, this.ROTATION_STEP);
	}

	public onRotateMouseUp = (): void => {
		this.stopRotateInterval();
	};

	public onUpClick(): void {
		this.startRotateInterval(-1 * this.ROTATION_STEP, 0);
	}

	public start(): void {
		if (this.viewer) {
			return;
		}

		const mediaElement = this.vgApi.mediaElement;

		this.observerTarget = mediaElement.parentElement;

		this.zone.runOutsideAngular(() => {
			// initialize the viewer
			this.viewer = new VBrick360Viewer(this.observerTarget, mediaElement);
			this.viewer.render()
				.then(() => {
					// start observing to size the viewer appropriately
					this.observer.observe(this.observerTarget);

					// click on the viewer sets focus to the vgElement so that key events work
					this.viewer.domElement.addEventListener('click', () => this.vgElement.focus());

					// initialize the mouse helper
					this.mouseHelper = new VBrick360MouseHelper(this.viewer, document);

					this.vgElement.tabIndex = 0; // must be focusable to faciliate key events
					this.vgElement.addEventListener('keydown', this.onKeyDown);
				});
		});
	}

	public stop(): void {
		if (!this.viewer) {
			return;
		}

		this.stopRotateInterval();

		this.mouseHelper.destroy();

		// stop ResizeObserver
		this.observer.unobserve(this.observerTarget);

		// remove key listener
		this.vgElement.removeEventListener('keydown', this.onKeyDown);

		// destroy the viewer
		this.viewer.destroy();
		this.viewer = this.mouseHelper = null;
	}

	private initResizeObserver(): void {
		this.resizeObserverPromise = import(/* webpackChunkName: "resize-observer-polyfill" */ 'resize-observer-polyfill')
			.then(resizeObserverImports =>
				this.observer = new resizeObserverImports.default(() => this.onResize())
			);
	}

	private onActiveSourceChange(): void {
		if (this.isEnabled && this.viewer) {
			this.viewer.forceUpdate();
		}
	}

	private onIsEnabledChange(): void {
		if (this.isEnabled) {
			if (!this.resizeObserverPromise) {
				this.initResizeObserver();
			}

			this.resizeObserverPromise
				.then(() => this.start());
		} else {
			this.stop();
		}
	}

	private onKeyDown = (event: KeyboardEvent): boolean => {
		const keyCode: number = event.which;
		let latDelta: number = 0;
		let lonDelta: number = 0;

		switch (keyCode) {
			case 37: // left
				lonDelta = -1;
				break;

			case 38: // up
				latDelta = -1;
				break;

			case 39: // right
				lonDelta = 1;
				break;

			case 40: // down
				latDelta = 1;
				break;
		}

		this.rotateByDelta(latDelta * this.ROTATION_STEP, lonDelta * this.ROTATION_STEP);

		return latDelta + lonDelta === 0;
	};

	private rotateByDelta(latDelta: number, lonDelta: number): void {
		const currentCoords = this.viewer.currentCoordinates;

		this.viewer.setCoordinates(currentCoords.lat + latDelta, currentCoords.lon + lonDelta);
	}

	private startRotateInterval(latDelta: number, lonDelta: number): void {
		document.addEventListener('mouseup', this.onRotateMouseUp);

		// initial
		this.rotateByDelta(latDelta, lonDelta);

		// get the interval started
		this.rotateIntervalRef = window.setInterval(() => this.rotateByDelta(latDelta, lonDelta), 100);
	}

	private stopRotateInterval(): void {
		document.removeEventListener('mouseup', this.onRotateMouseUp);

		if (this.rotateIntervalRef) {
			window.clearInterval(this.rotateIntervalRef);
			this.rotateIntervalRef = null;
		}
	}
}
